"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { useSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { getCsrfToken } from "next-auth/react";
import { UserProfile } from "@/utils/types";
import { PageLayout } from "@/components/ui/PageLayout";
import { PersonalInfoSection } from "./PersonalInfoSection";
import { EmailVerificationSection } from "./EmailVerificationSection";
import { LinkedAccountsSection } from "./LinkedAccountsSection";
import { PasskeysSection } from "./PasskeysSection";
import { StellarWalletsSection } from "./StellarWalletsSection";
import { NotificationsSection } from "./NotificationsSection";

export default function Profile() {
  const router = useRouter();
  const pathname = usePathname();
  const { data: session, status } = useSession();

  const [csrfToken, setCsrfToken] = useState("");
  const [error, setError] = useState("");
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [profileData, setProfileData] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const response = await fetch("/api/auth/csrf");
        if (response.ok) {
          const data = await response.json() as { csrfToken?: string };
          if (data.csrfToken) {
            setCsrfToken(data.csrfToken);
          }
        } else {
          const token = await getCsrfToken();
          if (token) setCsrfToken(token);
        }
      } catch {
        const token = await getCsrfToken();
        if (token) setCsrfToken(token);
      }
    })();

    if (status === "loading") return;
    if (session?.user.accessToken) {
      void fetchUserProfile();
    } else {
      router.push("/signup?mode=login&next=" + pathname);
    }
  }, [session, status, router, pathname]);

  async function fetchUserProfile(withLoading = true) {
    if (withLoading) setLoadingProfile(true);
    try {
      const res = await fetch("/api/auth/profile/one");
      if (!res.ok) {
        console.error("[Profile] GET /api/auth/profile/one failed:", res.status, await res.text());
        return;
      }
      const data = (await res.json()) as UserProfile;
      setProfileData(data);
    } catch (err) {
      console.error("[Profile] Error fetching user profile:", err);
    } finally {
      if (withLoading) setLoadingProfile(false);
    }
  }

  function handleError(message: string) {
    setError(message);
    setShowErrorDialog(true);
  }

  return (
    <PageLayout>
      <div style={{ maxWidth: "680px", margin: "0 auto", width: "100%" }}>
        <div style={{ marginBottom: "32px" }}>
          <h1
            style={{
              fontFamily: "'Instrument Serif', Georgia, serif",
              fontStyle: "italic",
              fontSize: "36px",
              color: "#f0ece4",
              letterSpacing: "-0.02em",
              fontWeight: 400,
              lineHeight: 1.1,
              margin: 0,
            }}
          >
            Profile
          </h1>
          <p style={{ fontSize: "11px", color: "#888", marginTop: "6px" }}>
            Manage your account settings and preferences
          </p>
        </div>

        {loadingProfile ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "80px", paddingBottom: "80px" }}>
            <p style={{ fontSize: "12px", color: "#4a4a4a" }}>Loading profile...</p>
          </div>
        ) : (
          <div>
            <PersonalInfoSection
              profileData={profileData}
              session={session}
              onProfileUpdated={() => { void fetchUserProfile(); }}
              onError={handleError}
            />
            <EmailVerificationSection
              profileData={profileData}
              session={session}
              onProfileUpdated={() => { void fetchUserProfile(); }}
            />
            <LinkedAccountsSection
              profileData={profileData}
              csrfToken={csrfToken}
              session={session}
              onProfileUpdated={() => { void fetchUserProfile(); }}
            />
            <PasskeysSection
              profileData={profileData}
              onProfileUpdated={() => { void fetchUserProfile(); }}
            />
            <StellarWalletsSection
              profileData={profileData}
              onProfileUpdated={() => { void fetchUserProfile(); }}
            />
            <NotificationsSection profileData={profileData} />
          </div>
        )}
      </div>

      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription className="text-red-500">{error}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
